import { useState, useEffect, useCallback } from "react"
import { truncateHash, calculateOptimalTruncation } from "../utils/hashutils"

/**
 * Hook pour tronquer les hash de manière responsive
 * @param {Object} options - Options de configuration
 * @returns {Object} Objet avec la fonction truncate et l'état actuel
 */
export function useTruncatedHash(options = {}) {
  // État pour suivre la taille de l'écran
  const [screenSize, setScreenSize] = useState("md")

  // Configuration pour différentes tailles d'écran
  const config = {
    xs: { start: 8, end: 8 },
    sm: { start: 8, end: 8 },
    md: { start: 8, end: 8 },
    lg: { start: 8, end: 8 },
    xl: { start: 12, end: 12 },
    ...options,
  }

  // Effet pour mettre à jour la taille de l'écran lorsqu'elle change
  useEffect(() => {
    // Fonction pour déterminer la taille de l'écran
    const updateScreenSize = () => {
      const width = window.innerWidth
      if (width < 640) setScreenSize("xs")
      else if (width < 768) setScreenSize("sm")
      else if (width < 1024) setScreenSize("md")
      else if (width < 1280) setScreenSize("lg")
      else setScreenSize("xl")
    }

    // Définir la taille initiale
    updateScreenSize()

    // Ajouter un écouteur pour mettre à jour la taille lorsque la fenêtre est redimensionnée
    window.addEventListener("resize", updateScreenSize)

    // Supprimer l'écouteur lorsque le composant est démonté
    return () => window.removeEventListener("resize", updateScreenSize)
  }, [])

  // Fonction de troncature qui utilise la taille d'écran actuelle
  const truncate = useCallback(
    (str, customConfig = {}) => {
      // Fusionner la configuration par défaut avec la configuration personnalisée
      const mergedConfig = { ...config, ...customConfig }
      const { start, end } = mergedConfig[screenSize]

      return truncateHash(str, start, end)
    },
    [screenSize],
  )

  // Fonction pour tronquer en fonction de l'élément conteneur
  const truncateByElement = useCallback((str, element, minChars = 4, maxChars = 10) => {
    if (!element) return truncateHash(str)

    const { start, end } = calculateOptimalTruncation(element, minChars, maxChars)
    return truncateHash(str, start, end)
  }, [])

  return {
    truncate,
    truncateByElement,
    screenSize,
  }
}
